import React, { Fragment, useContext, useEffect, useState } from 'react'
import Layout from './Layout'
import { MessageContext } from './Contexts'
import { Typography, TextField, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Grid, FormHelperText, FormControl } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles';
import { Autocomplete } from '@mui/material';
import GridCard from './GridCard'
import StartTaskButton from './StartTaskButton'
import { StatsListQueryGroups, StatsQuery, StatsQueryDetails } from 'modules/Rest'
import { startOfDay, addDays, setMonth, setDate, format, isAfter, subYears, getTime } from 'date-fns'
import { useParams } from 'react-router'
import GridContainer from './GridContainer'
import AButton from './AButton'
import { chunk } from 'lodash'
import { LoadingDotsIcon } from './Icons'
import { useSelectedDataRoom } from 'hooks/useSelectedDataRoom'

const monthNames = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

const monthNumbers = {
  'January': 0,
  'February': 1,
  'March': 2,
  'April': 3,
  'May': 4,
  'June': 5,
  'July': 6,
  'August': 7,
  'September': 8,
  'October': 9,
  'November': 10,
  'December': 11,
}

const CALENDAR_CHUNK_SIZE = 30
const DEFAULT_NUM_DAYS = 30

// if the requested month has a higher number than the current month, it means we want that month for last year
const getMonth = (month) =>
  isAfter(setMonth(new Date(), month), new Date()) ? subYears(setMonth(new Date(), month), 1) : setMonth(new Date(), month)

const getMonthStartAndEnd = (chunks, month, thisMonth) => {
  const days = chunks
    .map((chunk) => chunk[month]) // extract days from month
    .flat() // Make one array out of them
    .filter(Number) // remove non numeric values
  const firstDay = Math.min(...days)
  const lastDay = Math.max(...days)
  return [startOfDay(setDate(thisMonth, firstDay)), startOfDay(setDate(thisMonth, lastDay + 1))]
}

const startOfNextDay = (refDate) => {
  return addDays(startOfDay(refDate), 1)
}

const startOfPrevDay = (refDate) => {
  return addDays(startOfDay(refDate), -1)
}

const createBreakpoints = (numDays, currTime) => {
  currTime = currTime ? currTime : new Date()
  let breakpoints = [startOfNextDay(currTime)]
  for (let i=0; i<numDays; i++) {
    breakpoints.push(startOfPrevDay(breakpoints[breakpoints.length - 1]))
  }
  return breakpoints.reverse().map(getTime)
}

const useStyles = makeStyles(() => ({
  table: {
    marginBottom: 15,
    border: 'none',
    borderCollapse: 'collapse',
    '& td, th': {
      padding: '1px 2px',
      minWidth: 20,
    },
    '& thead': {
      border: '1px solid lightgray',
      '& th': {
        cursor: 'pointer',
        textAlign: 'right',
        border: 'none',
      },
      '& th:first-child': {
        cursor: 'auto',
      },
      '& tr:first-child': {
        '& th': {
          textAlign: 'center',
        },
      },
    },
    '& tbody': {
      '& td': {
        border: 'none',
        cursor: 'pointer',
        textAlign: 'right',
      },
      '& td:first-child': {
        cursor: 'auto',
        textAlign: 'left',
        whiteSpace: 'nowrap',
      },
    },
    fontSize: '14px',
  },
  grayText: {
    color: 'darkgray',
  },
  tableHeadInactive: {
    fontWeight: 600,
  },
  tableActive: {
    fontWeight: 800,
  },
  noRows: {
    fontStyle: 'italic'
  },
  warningTopRow: {
    borderBottom: 'none',
  },
  warningBottomRow: {
    borderTop: 'none',
    whiteSpace: 'pre-line',
  },
  dateCell: {
    whiteSpace: 'nowrap',
  },
  showButton: {
    position: 'absolute',
    left: '50%',
    marginLeft: -45,
    bottom: 4,
  },
  exportButton: {
    marginTop: 15,
  },
  inputGrid: {
    paddingRight: 10,
    '& MuiFormControl-root': {
      marginTop: '2px',
    }
  },
  buttonGrid: {
    position: 'relative',
  },
  FormControl: {
    flex: 1,
    display: 'unset',
    textAlign: 'center',
    '& p': {textAlign: 'center'},
  },
  daysInput: {
    '& .MuiFormLabel-root': {
      top: -2
    }
  }
}))

export default function Statistics() {
  const classes = useStyles()
  const { dataRoom } = useSelectedDataRoom()
  const { messageDispatch } = useContext(MessageContext)
  const { dataRoomKey = '' } = useParams()

  const [queryGroups, setQueryGroups] = useState([])
  const [statsFilter, setStatsFilter] = useState('')
  const [numDays, setNumDays] = useState('')
  const [monthDayChunks, setMonthDayChunks] = useState()
  const [statCounts, setStatCounts] = useState()
  const [statDetails, setStatDetails] = useState('')
  const [activeTimestamp, setActiveTimestamp] = useState()
  const [loadingStats, setLoadingStats] = useState()
  const [loadingDetails, setLoadingDetails] = useState()

  const emptyStats = () => {
    setStatCounts(null)
    setMonthDayChunks(null)
    setStatDetails(null)
    setActiveTimestamp(null)
  }

  useEffect(() => {
    async function getQueryGroups() {
      emptyStats()
      try {
        const { data: { success, info, user_types, users } } = await StatsListQueryGroups(dataRoom)
        if (success) {
          setQueryGroups([
            ...user_types.map((user_type_name) => {
              return {
                user_type_name,
                label: 'User type:',
              }
            }),
            ...users.map((user_name) => {
              return {
                user_name,
                label: 'User:',
              }
            }),
          ])
        } else {
          setQueryGroups([])
          throw new Error(info ? info : 'An error occurred')
        }
      } catch (error) {
        messageDispatch({ type: 'warning', payload: error.message })
      }
    }
    if (dataRoom) getQueryGroups()
  }, [dataRoom, messageDispatch])

  const getStatCounts = async () => {
    setLoadingStats(true)
    emptyStats()

    const breakpoints = createBreakpoints(numDays ? numDays : DEFAULT_NUM_DAYS, new Date())

    try {
      const {data: { success, info, ...data }} = await StatsQuery({ data_room: dataRoom.data_room, ...statsFilter, breakpoints })
      if (success) {
        const chunkedData = { // Split data into display chunks
          dates: chunk(data.breakpoints.slice(0, -1), CALENDAR_CHUNK_SIZE),
          deletions: chunk(data.deletions, CALENDAR_CHUNK_SIZE),
          dr_downloads: chunk(data.dr_downloads, CALENDAR_CHUNK_SIZE),
          file_downloads: chunk(data.file_downloads, CALENDAR_CHUNK_SIZE),
          file_uploads: chunk(data.file_uploads, CALENDAR_CHUNK_SIZE),
          indexes: chunk(data.indexes, CALENDAR_CHUNK_SIZE),
          logins: chunk(data.logins, CALENDAR_CHUNK_SIZE),
          warnings: chunk(data.warnings, CALENDAR_CHUNK_SIZE),
        }
        setStatCounts(chunkedData)

        setMonthDayChunks( // Split data by month (for display)
          chunkedData.dates.map((chunk) => {
            return chunk.reduce((accumulator, date) => {
              const newDate = new Date(date)
              const month = newDate.getMonth()
              accumulator[monthNames[month]] = [newDate.getDate(), ...(accumulator[monthNames[month]] || [])]
              return accumulator
            }, {})
          })
        )
      } else {
        throw new Error(info ? info : 'An error occurred')
      }
    } catch (error) {
      messageDispatch({ type: 'warning', payload: error.message })
    }
    setLoadingStats(false)
  }

  const getStatDetails = async (startTime, endTime) => {
    setLoadingDetails(true)
    setStatDetails(null)
    try {
      const { data: { success, info, ...data } } = await StatsQueryDetails({ data_room: dataRoom.data_room, ...statsFilter, breakpoints: [startTime, endTime] })
      if (success) {
        setLoadingDetails(false)
        return {
          logins: data.logins[0],
          file_downloads: data.file_downloads[0],
          file_uploads: data.file_uploads[0],
          deletions: data.deletions[0],
          dr_downloads: data.dr_downloads[0],
          indexes: data.indexes[0],
          warnings: data.warnings[0],
        }
      } else {
        throw new Error(info ? info : 'An error occurred')
      }
    } catch (error) {
      setLoadingDetails(false)
      messageDispatch({ type: 'warning', payload: error.message })
      return null
    }
  }

  const monthDetails = async (month, monthIndex) => {
    const thisMonth = getMonth(monthIndex)
    const [firstDay, lastDay] = getMonthStartAndEnd(monthDayChunks, month, thisMonth)
    setActiveTimestamp(month)
    setStatDetails(await getStatDetails(firstDay.getTime(), lastDay.getTime()))
  }

  const dayDetails = async (timestamp) => {
    const date = new Date(timestamp)
    const firstDay = startOfDay(date)
    const lastDay = addDays(firstDay, 1)
    setActiveTimestamp(firstDay.getTime())
    setStatDetails(await getStatDetails(firstDay.getTime(), lastDay.getTime()))
  }

  return <Layout>
    {!dataRoomKey && <Box><Typography variant='h5'>Please select a data room</Typography></Box>}

    {dataRoom && (
      <GridContainer>
        <GridCard sm={12} md={9}>
          <Grid container>
            <Grid item xs={12}>
              <Typography variant='h5'>Statistics</Typography>
            </Grid>

            <Grid item sm={6} className={classes.inputGrid}>
              <Autocomplete
                options={queryGroups}
                getOptionLabel={({ user_type_name, user_name }) => user_type_name || user_name || ''}
                groupBy={({ label }) => label}
                filterSelectedOptions
                renderInput={(params) => <TextField {...params} label='User or user type' variant="standard"/>}
                value={statsFilter}
                onChange={(_event, value) => setStatsFilter(value)}
              />
            </Grid>

            <Grid item sm={3} className={classes.inputGrid}>
              <TextField
                sx={{ marginTop: '2px' }}
                variant="standard"
                label='Number of days'
                type='number'
                value={numDays}
                onChange={({ target: { value } }) => setNumDays(value)}
                className={classes.daysInput}
              />
            </Grid>

            <Grid item sm={3} className={classes.buttonGrid}>
              <AButton color='primary' onClick={getStatCounts} className={classes.showButton}>
                Show stats
              </AButton>
            </Grid>
          </Grid>

        </GridCard>

        <GridCard sm={12} md={3}>
          <Typography variant='h5'>
            Export
          </Typography>
          <Box display='flex'>
            <FormControl className={classes.FormControl}>
              <StartTaskButton
                args={dataRoom}
                url={`${process.env.REACT_APP_ENDPOINT_BASE_URL}/api/v1/stats/export?use_async=true`}
                stage_label='Requesting stats export'
                buttonText='Export stats'
                className={classes.exportButton}
              />
              <FormHelperText>
                Exports all statistics
              </FormHelperText>
            </FormControl>
          </Box>
        </GridCard>

        {(loadingStats || monthDayChunks) && (
          <GridCard sm={12}>
            {loadingStats && <LoadingDotsIcon size={6} vPadding={6} hPadding={6}/>}
            {monthDayChunks && (
              <>
                <Box flexDirection='row-reverse'>
                  {monthDayChunks.map((monthDays, i) => (
                    <table border='1' cellSpacing='0' key={i} className={classes.table}>
                      <thead>
                      <tr>
                        <th></th>
                        {Object.entries(monthDays).map(([monthName, dates]) => (
                          <th
                            colSpan={dates.length}
                            key={monthName}
                            onClick={() => {
                              monthDetails(monthName, monthNumbers[monthName], i)
                            }}
                            className={monthName === activeTimestamp ? classes.tableActive : classes.tableHeadInactive}
                          >
                            {monthName}
                          </th>
                        ))}
                      </tr>
                      <tr>
                        <th></th>
                        {statCounts.dates[i].map((timestamp, index) => {
                          const date = new Date(timestamp)
                          const day = date.getDate()
                          return (
                            <th
                              key={timestamp}
                              onClick={() => dayDetails(timestamp)}
                              className={timestamp === activeTimestamp ? classes.tableActive : classes.tableHeadInactive}
                            >
                              {day}
                            </th>
                          )
                        })}
                      </tr>
                      </thead>

                      <tbody>
                      <tr>
                        <td>Logins</td>
                        {statCounts.logins[i].map((count, index) => {
                          const timestamp = statCounts.dates[i][index]
                          return (
                            <td
                              key={index}
                              onClick={() => dayDetails(timestamp)}
                              className={timestamp === activeTimestamp ? classes.tableActive : null}
                            >
                              <span className={count === 0 ? classes.grayText : null}>
                                {count}
                              </span>
                            </td>
                          )
                        })}
                      </tr>
                      <tr>
                        <td>Downloads&nbsp;/&nbsp;views</td>
                        {statCounts.file_downloads[i].map((count, index) => {
                          const timestamp = statCounts.dates[i][index]
                          return (
                            <td
                              key={index}
                              onClick={() => dayDetails(timestamp)}
                              className={timestamp === activeTimestamp ? classes.tableActive : null}
                            >
                              <span className={count === 0 ? classes.grayText : null}>
                                {count}
                              </span>
                            </td>
                          )
                        })}
                      </tr>
                      <tr>
                        <td>Uploads</td>
                        {statCounts.file_uploads[i].map((count, index) => {
                          const timestamp = statCounts.dates[i][index]
                          return (
                            <td
                              key={index}
                              onClick={() => dayDetails(timestamp)}
                              className={timestamp === activeTimestamp ? classes.tableActive : null}
                            >
                              <span className={count === 0 ? classes.grayText : null}>
                                {count}
                              </span>
                            </td>
                          )
                        })}
                      </tr>
                      <tr>
                        <td>Deletions</td>
                        {statCounts.deletions[i].map((count, index) => {
                          const timestamp = statCounts.dates[i][index]
                          return (
                            <td
                              key={index}
                              onClick={() => dayDetails(timestamp)}
                              className={timestamp === activeTimestamp ? classes.tableActive : null}
                            >
                              <span className={count === 0 ? classes.grayText : null}>
                                {count}
                              </span>
                            </td>
                          )
                        })}
                      </tr>
                      <tr>
                        <td>Data room downloads</td>
                        {statCounts.dr_downloads[i].map((count, index) => {
                          const timestamp = statCounts.dates[i][index]
                          return (
                            <td
                              key={index}
                              onClick={() => dayDetails(timestamp)}
                              className={timestamp === activeTimestamp ? classes.tableActive : null}
                            >
                              <span className={count === 0 ? classes.grayText : null}>
                                {count}
                              </span>
                            </td>
                          )
                        })}
                      </tr>
                      <tr>
                        <td>Index&nbsp;downloads</td>
                        {statCounts.indexes[i].map((count, index) => {
                          const timestamp = statCounts.dates[i][index]
                          return (
                            <td
                              key={index}
                              onClick={() => dayDetails(timestamp)}
                              className={timestamp === activeTimestamp ? classes.tableActive : null}
                            >
                              <span className={count === 0 ? classes.grayText : null}>
                                {count}
                              </span>
                            </td>
                          )
                        })}
                      </tr>
                      <tr>
                        <td>Warnings</td>
                        {statCounts.warnings[i].map((count, index) => {
                          const timestamp = statCounts.dates[i][index]
                          return (
                            <td
                              key={index}
                              onClick={() => dayDetails(timestamp)}
                              className={timestamp === activeTimestamp ? classes.tableActive : null}
                            >
                              <span className={count === 0 ? classes.grayText : null}>
                                {count}
                              </span>
                            </td>
                          )
                        })}
                      </tr>
                      </tbody>
                    </table>
                  ))}
                </Box>
                <FormHelperText>
                  Click on a day or month to view detailed statistics
                </FormHelperText>
              </>
            )}
          </GridCard>
        )}

        {(statDetails || loadingDetails) && (
          <GridCard sm={12}>
            <Typography variant='h5'>
              Details: Logins
            </Typography>
            {loadingDetails ? (
              <LoadingDotsIcon size={6} vPadding={6} hPadding={6}/>
            ) : (
              <TableContainer>
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date and time</TableCell>
                      <TableCell>User name</TableCell>
                      <TableCell>User group</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {statDetails.logins && statDetails.logins.length ? (
                      statDetails.logins.map(({ time, user_name, user_type_name }, index) => (
                        <TableRow key={index}>
                          <TableCell className={classes.dateCell}>{format(time, 'yyyy-MM-dd HH:mm')}</TableCell>
                          <TableCell>{user_name}</TableCell>
                          <TableCell>{user_type_name}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell className={classes.noRows} colSpan={4}>
                          No entries for the selected period
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </GridCard>
        )}
        {(statDetails || loadingDetails) && (
          <GridCard sm={12}>
            <Typography variant='h5'>
              Details: Downloads&nbsp;/&nbsp;views
            </Typography>
            {loadingDetails ? (
              <LoadingDotsIcon size={6} vPadding={6} hPadding={6}/>
            ) : (
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Date and time</TableCell>
                    <TableCell>User name</TableCell>
                    <TableCell>User group</TableCell>
                    <TableCell>File&nbsp;/&nbsp;folder</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {statDetails.file_downloads && statDetails.file_downloads.length ? (
                    statDetails.file_downloads.map(({ time, user_name, user_type_name, file_name }, index) => (
                      <TableRow key={index}>
                        <TableCell className={classes.dateCell}>{format(time, 'yyyy-MM-dd HH:mm')}</TableCell>
                        <TableCell>{user_name}</TableCell>
                        <TableCell>{user_type_name}</TableCell>
                        <TableCell>{file_name}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell className={classes.noRows} colSpan={4}>
                        No entries for the selected period
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </GridCard>
        )}
        {(statDetails || loadingDetails) && (
          <GridCard sm={12}>
            <Typography variant='h5'>
              Details: Uploads
            </Typography>
            {loadingDetails ? (
              <LoadingDotsIcon size={6} vPadding={6} hPadding={6}/>
            ) : (
              <TableContainer>
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date and time</TableCell>
                      <TableCell>User name</TableCell>
                      <TableCell>User group</TableCell>
                      <TableCell>File</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {statDetails.file_uploads && statDetails.file_uploads.length ? (
                      statDetails.file_uploads.map(({ time, user_name, user_type_name, file_name }, index) => (
                        <TableRow key={index}>
                          <TableCell className={classes.dateCell}>{format(time, 'yyyy-MM-dd HH:mm')}</TableCell>
                          <TableCell>{user_name}</TableCell>
                          <TableCell>{user_type_name}</TableCell>
                          <TableCell>{file_name}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell className={classes.noRows} colSpan={4}>
                          No entries for the selected period
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </GridCard>
        )}
        {(statDetails || loadingDetails) && (
          <GridCard sm={12}>
            <Typography variant='h5'>
              Details: Deletions
            </Typography>
            {loadingDetails ? (
              <LoadingDotsIcon size={6} vPadding={6} hPadding={6}/>
            ) : (
              <TableContainer>
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date and time</TableCell>
                      <TableCell>User name</TableCell>
                      <TableCell>User group</TableCell>
                      <TableCell>File&nbsp;/&nbsp;folder</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {statDetails.deletions && statDetails.deletions.length ? (
                      statDetails.deletions.map(({ time, user_name, user_type_name, file_name }, index) => (
                        <TableRow key={index}>
                          <TableCell className={classes.dateCell}>{format(time, 'yyyy-MM-dd HH:mm')}</TableCell>
                          <TableCell>{user_name}</TableCell>
                          <TableCell>{user_type_name}</TableCell>
                          <TableCell>{file_name}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell className={classes.noRows} colSpan={4}>
                          No entries for the selected period
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </GridCard>
        )}
        {(statDetails || loadingDetails) && (
          <GridCard sm={12}>
            <Typography variant='h5'>
              Details: Data room downloads
            </Typography>
            {loadingDetails ? (
              <LoadingDotsIcon size={6} vPadding={6} hPadding={6}/>
            ) : (
              <TableContainer>
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date and time</TableCell>
                      <TableCell>User name</TableCell>
                      <TableCell>User group</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {statDetails.dr_downloads && statDetails.dr_downloads.length ? (
                      statDetails.dr_downloads.map(({ time, user_name, user_type_name }, index) => (
                        <TableRow key={index}>
                          <TableCell className={classes.dateCell}>{format(time, 'yyyy-MM-dd HH:mm')}</TableCell>
                          <TableCell>{user_name}</TableCell>
                          <TableCell>{user_type_name}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell className={classes.noRows} colSpan={4}>
                          No entries for the selected period
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </GridCard>
        )}
        {(statDetails || loadingDetails) && (
          <GridCard sm={12}>
            <Typography variant='h5'>
              Details: Index&nbsp;downloads
            </Typography>
            {loadingDetails ? (
              <LoadingDotsIcon size={6} vPadding={6} hPadding={6}/>
            ) : (
              <TableContainer>
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date and time</TableCell>
                      <TableCell>User name</TableCell>
                      <TableCell>User group</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {statDetails.indexes && statDetails.indexes.length ? (
                      statDetails.indexes.map(({ time, user_name, user_type_name }, index) => (
                        <TableRow key={index}>
                          <TableCell className={classes.dateCell}>{format(time, 'yyyy-MM-dd HH:mm')}</TableCell>
                          <TableCell>{user_name}</TableCell>
                          <TableCell>{user_type_name}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell className={classes.noRows} colSpan={4}>
                          No entries for the selected period
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </GridCard>
        )}
        {(statDetails || loadingDetails) && (
          <GridCard sm={12}>
            <Typography variant='h5'>
              Details: Warnings
            </Typography>
            {loadingDetails ? (
              <LoadingDotsIcon size={6} vPadding={6} hPadding={6}/>
            ) : (
              <TableContainer>
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date and time</TableCell>
                      <TableCell>File&nbsp;/&nbsp;folder</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {statDetails.warnings && statDetails.warnings.length ? (
                      statDetails.warnings.map(({ time, user_name, user_type_name, file_name, warning }, index) => (
                        <Fragment key={index}>
                          <TableRow>
                            <TableCell className={classes.warningTopRow + ' ' + classes.dateCell}>{format(time, 'yyyy-MM-dd HH:mm')}</TableCell>
                            <TableCell className={classes.warningTopRow}>{file_name}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className={classes.warningBottomRow} colSpan={2}>{warning}</TableCell>
                          </TableRow>
                        </Fragment>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell className={classes.noRows} colSpan={2}>
                          No entries for the selected period
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </GridCard>
        )}
      </GridContainer>
    )}
  </Layout>
}
